import PAYMENT_PROFILES_API from 'api/payment-profiles/v1'
import GridSpinner from 'components/UI/GridSpinner'
import formStyles from 'modules/Forms/common-form.module.scss'
import PaymentProfileCreateForm from 'modules/Forms/Finance/PaymentProfileCreate'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from 'react-router'
import { useHistory } from 'react-router-dom'
import useQueryParams from 'utils/hooks/useQueryParams'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import validator from 'utils/asyncValidator'
import validationSchema from 'modules/Forms/Finance/PaymentProfileCreate/validationSchema'
import BILLING_API_V1 from 'api/billing/v1'
import uniqBy from 'lodash/uniqBy'
import { getFormValues } from 'redux-form'
import { useSelector } from 'react-redux'
import union from 'lodash/union'
import PageHeader from 'components/PageHeader'

const Create = () => {
  const history = useHistory()
  const pathGen = useGeneratePathI18n()
  const selector = getFormValues('PaymentProfileCreate')
  const { i18n } = useSelector(({ app }) => app)

  const {
    params: { profileId },
  } = useRouteMatch()
  const { t } = useTranslation()
  const { query: { goBack } = {} } = useQueryParams()
  const [initialValues, setInitialValues] = useState()
  const [profileTitle, setProfileTitle] = useState('')
  const {
    query: { payment_method },
  } = useQueryParams()

  const { formValues } = useSelector((state) => ({
    formValues: selector(state),
  }))

  useEffect(() => {
    if (profileId) {
      PAYMENT_PROFILES_API.fetchById({ profileId })
        .then(({ response, error }) => {
          if (error) {
            throw error
          }

          setInitialValues(response.data)
          setProfileTitle(
            response.data.details?.organisation_name ||
              response.data.details?.lastname + ' ' + response.data.details?.firstname ||
              '',
          )
        })
        .catch(() => history.push(goBack ? goBack : pathGen(ROUTES.FINANCE_PAYMENT_PROFILES)))
    }
  }, [])

  const [availableMethods, setAvailableMethods] = useState([])
  const [loadingSettings, setLoadingSettings] = useState(false)

  useEffect(() => {
    setLoadingSettings(true)
    BILLING_API_V1.fetchPayerSettings({
      params: { payment_method },
    })
      .then(({ response }) => {
        if (!response?.data) {
          return setLoadingSettings(false)
        }

        setAvailableMethods(
          response.data.filter((method) =>
            payment_method ? method.payment_method === payment_method : method,
          ),
        )
      })
      .finally(() => {
        setLoadingSettings(false)
      })
  }, [i18n])

  const legalTypes = useMemo(
    () => availableMethods.reduce((acc, item) => union(acc, [item.legal_type]), []),
    [availableMethods, payment_method],
  )

  const legalTypesCountryCodes = useMemo(() => {
    return legalTypes.reduce((acc, legaltype) => {
      acc[legaltype] = availableMethods
        .filter((am) => am.legal_type === legaltype)
        .reduce((acc, method) => {
          acc = uniqBy(acc.concat(method.countries), 'id')
          return acc
        }, [])

      return acc
    }, {})
  }, [legalTypes, availableMethods])

  return (
    <div className={formStyles['formContainer']}>
      <div className={formStyles['formContainer__inner']}>
        <PageHeader className={'margin-bottom-32'}>
          {t('payment_profiles::new.header', {
            context: payment_method ? 'for_method' : profileId ? 'edit' : 'new',
            title: profileTitle,
            payment_method: `"${t(`billing::methods.${payment_method}`)}"`,
          })}
        </PageHeader>
        {profileId && !initialValues ? (
          <GridSpinner />
        ) : (
          <PaymentProfileCreateForm
            loadingSettings={loadingSettings}
            profileId={profileId}
            initialValues={initialValues}
            onClose={() => history.push(goBack ? goBack : pathGen(ROUTES.FINANCE_PAYMENT_PROFILES))}
            legalTypes={legalTypes}
            legalTypesCountryCodes={legalTypesCountryCodes}
            asyncValidate={validator(
              validationSchema({
                legalTypes,
                formValues,
              }),
            )}
          />
        )}
      </div>
    </div>
  )
}

export default Create
